import { useInfiniteQuery } from "@tanstack/react-query";
import { HistoryPage } from "./projectHistory.model";
import {
  fetchProjectHistory,
  ProjectHistoryApiError,
} from "./projectHistory.repository";

/** 404(비멤버 — 자동 재시도 무의미) 를 제외한 일시 오류의 재시도 횟수. */
const HISTORY_RETRY_COUNT = 2;

/** 이력 종류 필터 미지정("전체", API 파라미터 생략)을 나타내는 키 세그먼트. */
const ALL_HISTORY_TYPES = "all";

/**
 * 프로젝트 히스토리 조회 컨텍스트(프로젝트 × 이력 종류)를 식별하는 키.
 *
 * `useGetProjectHistory` 의 queryKey 와, 컨텍스트 전환 시 목록 상태를 리셋하는 화면 측
 * resetKey(`ProjectHistory`)가 모두 이 함수에서 파생된다 — 조회 축이 늘어나면 이 함수만
 * 고치면 두 곳이 함께 따라온다. resetKey 는 반환 배열을 `join` 으로 문자열화해 쓰므로
 * "키가 바뀔 때만 바뀐다"가 성립한다.
 *
 * `projectId` 는 화면 상태(셀렉트 값 — id 문자열, 미선택은 빈 문자열)와 조회 인자(number)
 * 양쪽 표현을 모두 받아 같은 세그먼트로 정규화한다.
 */
export const projectHistoryQueryKey = (
  projectId: number | string | undefined,
  historyType?: string | null
) =>
  [
    "projectHistory",
    projectId === undefined || projectId === "" ? undefined : Number(projectId),
    historyType ?? ALL_HISTORY_TYPES,
  ] as const;

export interface UseGetProjectHistoryProps {
  projectId: number | undefined;
  /** 이력 종류 필터 — null("전체")이면 파라미터 생략 요청. */
  historyType?: string | null;
}

/**
 * 프로젝트 히스토리 타임라인 무한 스크롤 쿼리 (react-query v4).
 *
 * ⚠️ `keepPreviousData: true` 이므로 queryKey(프로젝트·필터)가 바뀌어도 **새 데이터가 도착할
 * 때까지 직전 queryKey 의 데이터를 그대로 반환한다.** 필터 전환 깜빡임 방지가 목적이지만
 * 프로젝트 전환에도 똑같이 적용돼, 전환 직후 `data` 는 아직 이전 프로젝트의 이벤트일 수 있다.
 * 이 구간은 반환값의 `isPreviousData` 로만 구분되므로, "지금 선택된 프로젝트의 데이터"가
 * 필요한 호출자는 `data` 와 `isPreviousData` 를 반드시 함께 본다
 * (현재 유일한 호출자인 `useProjectHistory` 가 그 판정을 캡슐화해
 * `hasUnresolvedProjectSwitch` 로 노출).
 *
 * queryKey 구성은 design.md D3, next_cursor null → undefined 변환으로 마지막 페이지에서
 * hasNextPage 가 false 가 된다. 렌더 측은 `data.pages.flatMap((p) => p.items)` 로 평탄화한다.
 */
export const useGetProjectHistory = ({
  projectId,
  historyType,
}: UseGetProjectHistoryProps) => {
  return useInfiniteQuery(
    projectHistoryQueryKey(projectId, historyType),
    ({ pageParam }: { pageParam?: number }) =>
      fetchProjectHistory(projectId as number, {
        cursor: pageParam ?? null,
        historyType: historyType ?? null,
      }),
    {
      enabled: !!projectId,
      getNextPageParam: (last: HistoryPage) => last.next_cursor ?? undefined,
      // 비멤버 404 는 재시도해도 결과가 같다 — 그 외 일시 오류만 제한 재시도
      retry: (failureCount, error) => {
        if (error instanceof ProjectHistoryApiError && error.status === 404) {
          return false;
        }
        return failureCount < HISTORY_RETRY_COUNT;
      },
      // 필터 전환 시 이전 목록을 유지해 깜빡임 방지 —
      // 프로젝트 전환에도 동일하게 적용되므로 그 구간 구분은 호출자가 isPreviousData 로 한다
      keepPreviousData: true,
      // append-only 데이터라 포커스 재조회 불필요
      refetchOnWindowFocus: false,
    }
  );
};
